import React, { Children, cloneElement, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Card, {
	CardActions,
	CardBody,
	CardFooter,
	CardFooterLeft,
	CardFooterRight,
	CardHeader,
	CardLabel,
	CardTitle,
} from './bootstrap/Card';
import Button from './bootstrap/Button';
import Popovers from './bootstrap/Popovers';
import useDarkMode from '../hooks/useDarkMode';
import './Wizard.css';

export const WizardItem = ({ id, title, children, className, setActiveItemIndex, ...props }) => {
	return (
		<section
			id={id}
			className={classNames('wizard-item', className)}
			role='tabpanel'
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...props}>
			{children}
		</section>
	);
};
WizardItem.propTypes = {
	id: PropTypes.string.isRequired,
	children: PropTypes.node.isRequired,
	title: PropTypes.string,
	className: PropTypes.string,
	icon: PropTypes.string,
};
WizardItem.defaultProps = {
	className: null,
	title: null,
	icon: 'Assignment',
};

const Wizard = ({ children, onSubmit, isHeader, color, stretch, extIndex, setExtIndex, ...props }) => {
	const { themeStatus } = useDarkMode();
	const [intActiveItemIndex, setIntActiveItemIndex] = useState(0);

	const _setActiveIndex = (index) => {
		setIntActiveItemIndex(index);
		if (setExtIndex) {
			setExtIndex(index);
		}
	}

	if (!Array.isArray(children)) {
		children = [children]
	}
	children = children.filter(x => x)

	const childCount = children.length;

	const getTitleName = (i) => {
		return `Step ${i + 1}`;
	};

	const activeItemIndex = extIndex ?? intActiveItemIndex;

	const prevBtn = !!activeItemIndex && (
		<Button color={color} isLink onClick={() => _setActiveIndex(activeItemIndex - 1)}>
			Previous
		</Button>
	);

	const nextBtn = (
		<>
			<Button
				className={classNames({ 'd-none': childCount === activeItemIndex + 1 }, 'me-0')}
				aria-hidden={childCount === activeItemIndex + 1}
				color={color}
				isLight
				isDisable={children[activeItemIndex]?.props?.nextIndexTransitionDisabled}
				onClick={() => _setActiveIndex(activeItemIndex + 1)}>
				Next
			</Button>
			<Button
				className={classNames({ 'd-none': childCount !== activeItemIndex + 1 })}
				aria-hidden={childCount !== activeItemIndex + 1}
				type='submit'
				isDisable={children[activeItemIndex]?.props?.nextIndexTransitionDisabled}
				color={color}>
				Submit
			</Button>
		</>
	);

	return (
		// eslint-disable-next-line react/jsx-props-no-spreading
		<Card stretch={stretch} tag='form'
			onSubmit={(e) => {
				e.preventDefault();
				onSubmit()
			}}
			{...props}>
			{!!isHeader && (
				Children.length > 1 && <CardHeader className="m-0 p-0">
					{Children.map(children, (child, index) => (
						(Children.length > 1 || child.props.title) && <CardLabel className={index !== activeItemIndex ? 'd-none' : null} icon={child.props.icon ?? 'Assignment'} iconColor={color}>
							<CardTitle
								key={child.props.id}>
								{child.props.title || getTitleName(index)}
							</CardTitle>
						</CardLabel>
					))}
					{isHeader === 'withButton' && (
						<CardActions>
							{prevBtn}
							{nextBtn}
						</CardActions>
					)}
				</CardHeader>
			)}
			<CardBody className="m-0 p-0" isScrollable={!!stretch}>
				{children?.length > 1 && <div className='wizard-progress position-relative'>
					<div className='progress'>
						<div
							className={classNames('progress-bar', {
								[`bg-${color}`]: color !== 'primary',
							})}
							role='progressbar'
							style={{ width: `${(100 / (childCount - 1)) * activeItemIndex}%` }}
							aria-valuenow={(100 / (childCount - 1)) * activeItemIndex}
							aria-valuemin='0'
							aria-valuemax='100'
							aria-label='progress'
						/>
					</div>
					{Children.map(children, (child, index) => (
						<Popovers
							key={child.props.id}
							desc={child.props.title || getTitleName(index)}
							trigger='hover'>
							<button
								type='button'
								className={classNames(
									'wizard-progress-btn',
									'position-absolute p-0 top-0',
									'translate-middle',
									'btn btn-sm',
									{
										[`btn-${color}`]: activeItemIndex >= index,
										[`btn-${themeStatus}`]: activeItemIndex < index,
									},
									'rounded-pill',
								)}
								style={{
									left: `${(100 / (childCount - 1)) * index}%`,
								}}
								onClick={() => _setActiveIndex(index)}>
								{index + 1}
							</button>
						</Popovers>
					))}
				</div>}

				<div className='wizard'>
					{Children.map(children, (child, index) => {
						return index == activeItemIndex &&
							cloneElement(child, {
								className: index !== activeItemIndex ? 'd-none' : '',
								'aria-hidden': index !== activeItemIndex,
							})
					},
					)}
				</div>
			</CardBody>
			<CardFooter className="p-0 ml-3 mr-3" style={{ paddingTop: 0 }}>

				<CardFooterLeft>{prevBtn}</CardFooterLeft>
				<CardFooterRight>{nextBtn}</CardFooterRight>
			</CardFooter>
		</Card>
	);
};
Wizard.propTypes = {
	children: PropTypes.node.isRequired,
	color: PropTypes.oneOf([
		'primary',
		'secondary',
		'success',
		'info',
		'warning',
		'danger',
		'dark',
		'brand',
		'brand-two',
		'storybook',
	]),
	isHeader: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['withButton'])]),
	onSubmit: PropTypes.func.isRequired,
	stretch: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['full', 'semi'])]),
};
Wizard.defaultProps = {
	isHeader: false,
	color: 'primary',
	stretch: null,
};

export default Wizard;
